import { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useOverlayTriggerState } from 'react-stately'
import { styled } from 'styled-components'

import growth from '@/assets/images/illustrations/croissance.svg'
import forms from '@/assets/images/illustrations/formes.svg'
import RuleInput from '@/components/conversation/RuleInput'
import { CurrentSimulatorCard } from '@/components/CurrentSimulatorCard'
import { Condition } from '@/components/EngineValue/Condition'
import { EntrepriseDetailsCard } from '@/components/entreprise/EntrepriseDetailsCard'
import PageHeader from '@/components/PageHeader'
import { ACCUEIL, TrackPage } from '@/components/PianoAnalytics'
import { SimulateurCard } from '@/components/SimulateurCard'
import { SmallSimulateurCard } from '@/components/SmallSimulateurCard'
import { FromTop } from '@/components/ui/animate'
import { ForceThemeProvider } from '@/components/utils/DarkModeContext'
import {
	Body,
	Button,
	Container,
	Grid,
	H2,
	H3,
	Intro,
	Markdown,
	Message,
	Popover,
	Spacing,
} from '@/design-system'
import { Entreprise } from '@/domaine/Entreprise'
import { premiersMoisUrssaf } from '@/external-links/premiersMoisUrssaf'
import { usePageMetadata } from '@/hooks/usePageMetadata'
import { useQuestionList } from '@/hooks/useQuestionList'
import { useEntreprisesRepository } from '@/hooks/useRepositories'
import { useSetEntreprise } from '@/hooks/useSetEntreprise'
import useSimulationPublicodes from '@/hooks/useSimulationPublicodes'
import { useSimulatorsMetadata } from '@/hooks/useSimulatorsMetadata'
import { useNavigation } from '@/lib/navigation'
import SimulateurPageLayout from '@/pages/simulateurs/SimulateurPageLayout'
import { useSitePaths } from '@/sitePaths'
import { resetCompany } from '@/store/actions/companyActions'
import { companySituationSelector } from '@/store/selectors/company/companySituation.selector'
import { EngineProvider, useEngine } from '@/utils/publicodes/EngineContext'
import { evaluateQuestion } from '@/utils/publicodes/publicodes'

import { pourMonEntrepriseMetadata } from './metadata'
import { configPourMonEntreprise } from './simulationConfig'

export default function PourMonEntrepriseHome() {
	const { relativeSitePaths } = useSitePaths()

	return (
		<Routes>
			<Route
				path={relativeSitePaths.assistants['pour-mon-entreprise'].entreprise}
				element={<PourMonEntreprise />}
			/>
			<Route path="*" element={<PourMonEntreprise />} />
		</Routes>
	)
}

const externalLinks = [premiersMoisUrssaf]

function PourMonEntreprise() {
	const { t } = useTranslation()
	const simulateurs = useSimulatorsMetadata()
	const metadata = usePageMetadata(pourMonEntrepriseMetadata)
	const { isReady, engine } = useSimulationPublicodes(
		metadata,
		configPourMonEntreprise
	)
	const dispatch = useDispatch()
	const engineSiren = engine.evaluate('entreprise . SIREN').nodeValue
	const prevSiren = useRef(engineSiren)
	const [overwrite, setOverwrite] = useState(engineSiren === undefined)
	const { param, entreprise, entrepriseNotFound, entreprisePending } =
		useSirenFromParams(overwrite)
	const gérerPath = usePourMonEntreprisePath()

	const setEntreprise = useSetEntreprise()

	const updateEntreprise =
		overwrite &&
		!entreprisePending &&
		!entrepriseNotFound &&
		entreprise &&
		entreprise.siren !== engineSiren

	useEffect(() => {
		if (updateEntreprise) {
			setEntreprise(entreprise)
			setOverwrite(false)
		}
	}, [entreprise, setEntreprise, updateEntreprise])

	if (
		gérerPath &&
		(param == null || (entreprise?.siren && entreprise.siren !== param))
	) {
		return <Navigate to={gérerPath} replace />
	}

	if (
		(!param && !engineSiren) ||
		(!engineSiren && !overwrite && prevSiren.current) ||
		(param && entrepriseNotFound) ||
		(entreprise && !overwrite && !engineSiren)
	) {
		return <Navigate to="/404" replace />
	}

	const isAutoEntrepreneur =
		engine.evaluate('dirigeant . auto-entrepreneur').nodeValue === true

	return (
		<>
			<Helmet>
				<meta name="robots" content="noindex" />
			</Helmet>

			<TrackPage name={ACCUEIL} />

			<EngineProvider value={engine}>
				<SimulateurPageLayout
					metadata={metadata}
					isReady={isReady}
					externalLinks={externalLinks}
				>
					{param && engineSiren && param !== engineSiren && !overwrite && (
						<PopoverOverwriteSituation
							onOverwrite={() => {
								dispatch(resetCompany())
								setOverwrite(true)
							}}
						/>
					)}

					<PageHeader picture={growth}>
						<Intro>
							{t(
								'pages.assistants.pour-mon-entreprise.description',
								'Vous souhaitez vous verser un revenu ou embaucher ? Vous aurez à payer des cotisations et des impôts. Anticipez leurs montants grâce aux simulateurs adaptés à votre situation.'
							)}
						</Intro>
						{entreprisePending ? (
							<Message type="info" border={false}>
								<Intro>{t('messages.loading', 'Chargement en cours…')}</Intro>
							</Message>
						) : (
							<AskCompanyMissingDetails />
						)}
						<Spacing xl />
					</PageHeader>

					<Container
						backgroundColor={(theme) => theme.colors.bases.primary[600]}
					>
						<FromTop>
							<FormsImage src={forms} alt="" />
							<Spacing xs />
							<ForceThemeProvider forceTheme="dark">
								{t(
									'pages.assistants.pour-mon-entreprise.simulateurs',
									'Simulateurs pour votre entreprise'
								)}
							</ForceThemeProvider>
							<Grid
								container
								spacing={3}
								style={{
									position: 'relative',
								}}
							>
								<CurrentSimulatorCard fromGérer />

								<Condition expression="entreprise . imposition . IS">
									<Grid
										item
										xs={12}
										md={6}
										lg={4}
										style={{
											alignSelf: 'flex-end',
										}}
									>
										<Grid container spacing={3} columns={2}>
											<SmallSimulateurCard fromGérer {...simulateurs.is} />
											<SmallSimulateurCard
												fromGérer
												{...simulateurs.dividendes}
											/>
										</Grid>
									</Grid>
								</Condition>
							</Grid>
						</FromTop>
						<Spacing xl />
					</Container>
					{!isAutoEntrepreneur && (
						<FromTop>
							<H2>
								{t(
									'pages.assistants.pour-mon-entreprise.embauche',
									'Salariés et embauche'
								)}
							</H2>
							<Grid container spacing={3}>
								<SimulateurCard fromGérer {...simulateurs['salarié']} />
								<SimulateurCard
									fromGérer
									{...simulateurs['activité-partielle']}
								/>
							</Grid>
						</FromTop>
					)}
				</SimulateurPageLayout>
			</EngineProvider>
		</>
	)
}

const AskCompanyMissingDetails = () => {
	const { t } = useTranslation()
	const [questions, onQuestionAnswered] = useQuestionList()
	const engine = useEngine()

	return (
		<>
			<EntrepriseDetailsCard showSituation headingTag="h2" />
			{!!questions.length && (
				<>
					<Body
						style={{
							marginBottom: '-0.5rem',
						}}
					>
						{t(
							'pages.assistants.pour-mon-entreprise.questions',
							'Répondez aux questions suivantes pour découvrir les simulateurs et assistants adaptés à votre situation :'
						)}
					</Body>

					{questions.map((question) => (
						<FromTop key={question.dottedName}>
							<H3>
								<Markdown options={{ forceInline: true }}>
									{evaluateQuestion(engine, question) ?? ''}
								</Markdown>
							</H3>
							<RuleInput
								dottedName={question.dottedName}
								onChange={onQuestionAnswered(question.dottedName)}
							/>
						</FromTop>
					))}
				</>
			)}
		</>
	)
}

const PopoverOverwriteSituation = ({
	onOverwrite,
	onCancel,
}: {
	onOverwrite?: () => void
	onCancel?: () => void
}) => {
	const { t } = useTranslation()
	const state = useOverlayTriggerState({ defaultOpen: true })

	if (!state.isOpen) {
		return null
	}

	return (
		<Popover
			title={t(
				'warning.overwrite.situation',
				'Voulez-vous écraser votre situation ?'
			)}
			onClose={() => {
				state.close()
				onCancel?.()
			}}
			isDismissable
			small
		>
			<Message type="info">
				<Body>
					{t(
						'pages.assistants.pour-mon-entreprise.situation-overwrite.question',
						'Nous avons détecté une ancienne situation, êtes-vous sûr de vouloir l’écraser ?'
					)}
				</Body>
			</Message>
			<Grid
				container
				style={{
					justifyContent: 'end',
				}}
				spacing={2}
			>
				<Grid item>
					<Button
						size="XS"
						onPress={() => {
							onOverwrite?.()
						}}
					>
						{t(
							'pages.assistants.pour-mon-entreprise.situation-overwrite.écraser',
							'Écraser'
						)}
					</Button>
				</Grid>
				<Grid item>
					<Button
						size="XS"
						light
						onPress={() => {
							state.close()
							onCancel?.()
						}}
					>
						{t(
							'pages.assistants.pour-mon-entreprise.situation-overwrite.annuler',
							'Annuler'
						)}
					</Button>
				</Grid>
			</Grid>
		</Popover>
	)
}

const usePourMonEntreprisePath = () => {
	const { absoluteSitePaths } = useSitePaths()
	const { generatePath } = useNavigation()
	const company = useSelector(companySituationSelector)

	if (company['entreprise . SIREN']) {
		const siren = (company['entreprise . SIREN'] as string).replace(/'/g, '')

		return generatePath(
			absoluteSitePaths.assistants['pour-mon-entreprise'].entreprise,
			{ entreprise: siren }
		)
	}

	return null
}

const useSirenFromParams = (overwrite: boolean) => {
	const { matchPath } = useNavigation()
	const { absoluteSitePaths } = useSitePaths()
	const match = matchPath(
		absoluteSitePaths.assistants['pour-mon-entreprise'].entreprise
	)
	const param = match?.params.entreprise
	const [entreprise, setEntreprise] = useState<Entreprise | null>(null)

	const [entreprisePending, setEntreprisePending] = useState(false)
	const [entrepriseNotFound, setEntrepriseNotFound] = useState(false)
	const entreprisesRepository = useEntreprisesRepository()

	useEffect(() => {
		if (!param || !overwrite) {
			return
		}
		setEntreprisePending(true)
		entreprisesRepository
			.rechercheTexteLibre(param)
			.then((entreprises) => {
				setEntreprisePending(false)
				if (!entreprises || !entreprises.length) {
					return setEntrepriseNotFound(true)
				}
				setEntreprise(entreprises[0])
			})
			.catch((error) => {
				setEntrepriseNotFound(true)
				setEntreprisePending(false)
				// eslint-disable-next-line no-console
				console.error(error)
			})
	}, [param, overwrite])

	return {
		param,
		entreprise,
		entreprisePending,
		entrepriseNotFound,
	}
}

const FormsImage = styled.img`
	position: absolute;
	height: 25rem;
	transform: rotate(180deg);
	top: -1px;
	z-index: 0;

	@media (max-width: ${({ theme }) => theme.breakpointsWidth.md}) {
		display: none;
	}

	@media (min-width: ${({ theme }) => theme.breakpointsWidth.md}) {
		right: 5rem;
		height: 12rem;
	}

	@media (min-width: ${({ theme }) => theme.breakpointsWidth.lg}) {
		right: 8.5rem;
		height: 20rem;
	}

	@media (min-width: ${({ theme }) => theme.breakpointsWidth.xl}) {
		right: 10rem;
		height: 23rem;
	}
`
